// client/src/components/plataforma/users/tabs/UserPasswordTab.js
import React, { useState, useMemo } from 'react';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, AlertTriangle, Settings, Save, Loader2 } from 'lucide-react';
import useAlert from '../../../../hooks/useAlert';

const UserPasswordTab = ({ formData, onChange, isEditing, formErrors = {}, onSave, isSaving = false }) => {
    const { setAlert } = useAlert();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    // Controle local para mostrar os campos apenas quando clica em Editar
    const [localIsEditing, setLocalIsEditing] = useState(!isEditing);

    const handlePasswordChange = (field, value) => {
        onChange({
            target: {
                name: field,
                value: value
            }
        });
    };

    const passwordStrength = useMemo(() => {
        const password = formData.password || '';
        if (!password) return { strength: 0, label: 'Vazia', color: 'text-gray-400', bg: 'bg-gray-200' };
        
        let strength = 0;
        if (password.length >= 6) strength += 1;
        if (password.length >= 8) strength += 1;
        if (/[A-Z]/.test(password)) strength += 1;
        if (/[0-9]/.test(password)) strength += 1;
        if (/[^A-Za-z0-9]/.test(password)) strength += 1;
        
        const levels = [
            { label: 'Muito Fraca', color: 'text-red-600', bg: 'bg-red-500' },
            { label: 'Fraca', color: 'text-red-500', bg: 'bg-red-400' },
            { label: 'Razoável', color: 'text-orange-500', bg: 'bg-orange-500' },
            { label: 'Boa', color: 'text-yellow-600', bg: 'bg-yellow-500' },
            { label: 'Forte', color: 'text-green-600', bg: 'bg-green-500' },
            { label: 'Muito Forte', color: 'text-green-700', bg: 'bg-green-600' }
        ];
        
        return {
            strength,
            ...levels[Math.min(strength, 5)]
        };
    }, [formData.password]);

    const passwordsMatch = formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;
    const hasPasswordError = formData.confirmPassword && !passwordsMatch;
    const isPasswordValidLength = formData.password && formData.password.length >= 6;

    const handleCancel = () => {
        // Limpa os campos de senha ao cancelar
        onChange({ target: { name: 'password', value: '' } });
        onChange({ target: { name: 'confirmPassword', value: '' } });
        setLocalIsEditing(false);
    };

    const handleSaveSubmit = () => {
        if (!formData.password) {
            setAlert('A senha não pode estar vazia.', 'warning');
            return;
        }
        
        if (!isPasswordValidLength) {
            setAlert('A senha deve ter no mínimo 6 caracteres.', 'warning');
            return;
        }

        if (!passwordsMatch) {
            setAlert('As senhas não coincidem.', 'warning');
            return;
        }

        if (onSave) {
            onSave();
            // Só fecha o modo de edição depois de salvar com sucesso (lógica cuidada pelo componente pai, mas fechamos a UI aqui preventivamente)
            setLocalIsEditing(false);
            
            // Limpa os campos para evitar que fiquem preenchidos após o save (já que a senha não é devolvida pela API por segurança)
            setTimeout(() => {
                onChange({ target: { name: 'password', value: '' } });
                onChange({ target: { name: 'confirmPassword', value: '' } });
            }, 500);
        }
    };

    return (
        <div className="form-content">
            
            {/* ✅ CABEÇALHO COM BOTÕES PADRÃO */}
            <div className="form-header flex items-center justify-between mb-6">
                <div>
                    <h3 className="form-title flex items-center mb-0">
                        <Lock size={20} className="mr-2 text-primary" />
                        Segurança
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        {isEditing 
                            ? 'Altere a senha de acesso do usuário.'
                            : 'Defina a senha inicial do usuário.'}
                    </p>
                </div>

                {isEditing && (
                    !localIsEditing ? (
                        <button 
                            type="button" 
                            onClick={() => setLocalIsEditing(true)} 
                            className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                        >
                            <Settings size={16} className="mr-2 text-gray-500" /> Alterar Senha
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button 
                                type="button" 
                                onClick={handleCancel} 
                                className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                            >
                                <XCircle size={16} className="mr-2 text-gray-500" /> Cancelar
                            </button>
                            <button 
                                type="button" 
                                onClick={handleSaveSubmit} 
                                disabled={isSaving || hasPasswordError || !isPasswordValidLength} 
                                className="action-button action-button-primary bg-primary text-white shadow-sm"
                            >
                                {isSaving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                                Salvar Nova Senha
                            </button>
                        </div>
                    )
                )}
            </div>

            {/* CONTEÚDO SÓ APARECE SE ESTIVER CRIANDO OU SE CLICAR EM EDITAR */}
            {(localIsEditing || !isEditing) && (
                <div className="form-section animate-fadeIn">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3 mb-6">
                        <Lock className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
                        <div>
                            <h4 className="font-medium text-blue-800 mb-1">
                                {isEditing ? 'Alteração de Senha' : 'Definição de Senha Inicial'}
                            </h4>
                            <p className="text-sm text-blue-700">
                                {isEditing 
                                    ? 'Preencha os campos abaixo para alterar a senha atual do usuário.'
                                    : 'Defina uma senha segura para o novo usuário. Ele poderá alterá-la posteriormente.'
                                }
                            </p>
                        </div>
                    </div>

                    <div className="form-grid-container">
                        {/* Campo: Nova Senha */}
                        <div className="form-group">
                            <label className="required">
                                {isEditing ? 'Nova Senha' : 'Senha'}
                            </label>
                            <div className="input-with-icon relative">
                                <Lock size={16} className="input-icon absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={formData.password || ''}
                                    onChange={(e) => handlePasswordChange('password', e.target.value)}
                                    className={`form-input pl-10 pr-10 ${formErrors.password ? 'border-red-500' : ''}`}
                                    placeholder={isEditing ? "Digite a nova senha" : "Mínimo 6 caracteres"}
                                    minLength="6"
                                    autoComplete="new-password"
                                    required={!isEditing}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                    tabIndex="-1"
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            {formErrors.password && <div className="text-red-500 text-sm mt-1">{formErrors.password}</div>}

                            {/* Indicador de Força da Senha */}
                            {formData.password && (
                                <div className="mt-3 animate-fadeIn">
                                    <div className="flex items-center justify-between text-xs mb-1">
                                        <span className="text-gray-500">Força da senha:</span>
                                        <span className={`font-medium ${passwordStrength.color}`}>
                                            {passwordStrength.label}
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                        <div 
                                            className={`h-full transition-all duration-500 ease-out ${passwordStrength.bg}`}
                                            style={{ width: `${Math.max((passwordStrength.strength / 5) * 100, 5)}%` }}
                                        />
                                    </div>
                                    <div className="form-hint mt-2">
                                        <div className={`flex items-center mt-1 ${formData.password.length >= 6 ? 'text-green-600' : ''}`}>
                                            {formData.password.length >= 6 ? <CheckCircle size={12} className="mr-1" /> : <div className="w-3 h-3 border border-gray-300 rounded-full mr-1" />}
                                            Mínimo 6 caracteres
                                        </div>
                                        <div className={`flex items-center mt-1 ${/[A-Z]/.test(formData.password) ? 'text-green-600' : ''}`}>
                                            {/[A-Z]/.test(formData.password) ? <CheckCircle size={12} className="mr-1" /> : <div className="w-3 h-3 border border-gray-300 rounded-full mr-1" />}
                                            Letra maiúscula
                                        </div>
                                        <div className={`flex items-center mt-1 ${/[0-9]/.test(formData.password) ? 'text-green-600' : ''}`}>
                                            {/[0-9]/.test(formData.password) ? <CheckCircle size={12} className="mr-1" /> : <div className="w-3 h-3 border border-gray-300 rounded-full mr-1" />}
                                            Número
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Campo: Confirmar Senha */}
                        {(formData.password || (!isEditing && !formData.password)) && (
                            <div className="form-group animate-fadeIn">
                                <label className="required">
                                    Repetir Senha
                                </label>
                                <div className="input-with-icon relative">
                                    <Lock size={16} className="input-icon absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        name="confirmPassword"
                                        value={formData.confirmPassword || ''}
                                        onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                                        className={`form-input pl-10 pr-10 ${
                                            hasPasswordError 
                                                ? 'border-red-500' 
                                                : passwordsMatch 
                                                    ? 'border-green-500 focus:ring-green-500 focus:border-green-500' 
                                                    : ''
                                        }`}
                                        placeholder="Digite a senha novamente"
                                        required={!isEditing}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                        tabIndex="-1"
                                    >
                                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                                {formErrors.confirmPassword && <div className="text-red-500 text-sm mt-1">{formErrors.confirmPassword}</div>}
                                
                                {/* Mensagens de Validação */}
                                {hasPasswordError && (
                                    <div className="text-red-600 text-xs mt-2 flex items-center animate-fadeIn">
                                        <XCircle size={12} className="mr-1" />
                                        As senhas não coincidem
                                    </div>
                                )}
                                
                                {passwordsMatch && formData.password && (
                                    <div className="text-green-600 text-xs mt-2 flex items-center animate-fadeIn">
                                        <CheckCircle size={12} className="mr-1" />
                                        Senhas coincidem
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Alerta de Segurança para Edição */}
                    {isEditing && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3 mt-6">
                            <AlertTriangle className="text-yellow-600 mt-0.5 flex-shrink-0" size={20} />
                            <div>
                                <h4 className="font-medium text-yellow-800 mb-1">
                                    Atenção
                                </h4>
                                <p className="text-sm text-yellow-700">
                                    Ao alterar a senha, o usuário será desconectado de todos os dispositivos e precisará fazer login novamente com a nova credencial.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {!localIsEditing && isEditing && (
                <div className="py-12 flex flex-col items-center justify-center text-gray-500 border border-dashed border-gray-200 rounded-lg bg-gray-50">
                    <Lock size={48} className="text-gray-300 mb-3" />
                    <p className="font-medium">Segurança da Conta</p>
                    <p className="text-sm mt-1">Clique em "Alterar Senha" para definir uma nova credencial.</p>
                </div>
            )}
        </div>
    );
};

export default UserPasswordTab;